import React, { useEffect, useMemo, useState } from "react";
import {
  adminListUsers, adminDeleteUser, adminUpdateUser,
  adminGrantPro, adminRevokePro, adminResetPassword,
  adminListInvites, adminCreateInvite, adminRevokeInvite,
  adminListConditionSuggestions, adminUpdateConditionSuggestion, adminDeleteConditionSuggestion,
  adminListNewsletterSubscribers, adminDeleteNewsletterSubscriber, adminNewsletterExportUrl,
  getToken,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import {
  UsersThree, Trash, ShieldCheck, ArrowsClockwise, PencilSimple,
  Crown, KeyReturn, Copy, EnvelopeOpen, PaperPlaneTilt, X, Check, BookOpenText,
  EnvelopeSimple, DownloadSimple,
} from "@phosphor-icons/react";

const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "", label: "(none / free)" },
  { value: "complimentary", label: "complimentary (admin-granted)" },
  { value: "grandfathered", label: "grandfathered" },
  { value: "active", label: "active (paid)" },
  { value: "trialing", label: "trialing" },
  { value: "past_due", label: "past_due" },
  { value: "canceled", label: "canceled" },
];

function PlanBadge({ user }) {
  const status = user.subscription_status || "";
  const isPro = user.is_pro || user.plan === "pro";
  const label = !isPro ? "FREE"
    : status === "complimentary" ? "PRO • COMP"
    : status === "grandfathered" ? "PRO • GF"
    : status === "active" ? "PRO • PAID"
    : status === "trialing" ? "PRO • TRIAL"
    : status === "past_due" ? "PRO • DUE"
    : "PRO";
  const cls = !isPro ? "plan-badge plan-badge--free"
    : status === "complimentary" ? "plan-badge plan-badge--comp"
    : status === "active" || status === "trialing" ? "plan-badge plan-badge--paid"
    : "plan-badge plan-badge--gf";
  return <span className={cls}>{label}</span>;
}

function EditUserModal({ user, onClose, onSaved, isSelf }) {
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role || "user");
  const [plan, setPlan] = useState(user.plan || "free");
  const [subStatus, setSubStatus] = useState(user.subscription_status || "");
  const [emailVerified, setEmailVerified] = useState(!!user.emailVerified);
  const [notes, setNotes] = useState(user.notes || "");
  const [tagsText, setTagsText] = useState((user.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function save() {
    setBusy(true); setErr("");
    try {
      const updates = {
        email: email.trim().toLowerCase(),
        role, plan,
        subscription_status: subStatus,
        emailVerified,
        notes,
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await adminUpdateUser(user.id, updates);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" data-testid="edit-user-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel modal-panel--lg">
        <div className="modal-header">
          <PencilSimple size={16} weight="bold" />
          <span>EDIT USER</span>
          <button className="modal-close" onClick={onClose} data-testid="edit-user-close"><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body modal-body--scroll">
          <div className="form-grid">
            <label className="field">
              <span className="field-label">EMAIL</span>
              <input className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-user-email" />
            </label>
            <label className="field">
              <span className="field-label">ROLE</span>
              <select className="field-input" value={role} onChange={(e) => setRole(e.target.value)} disabled={isSelf} data-testid="edit-user-role">
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
              {isSelf && <div className="field-hint">You cannot demote your own admin account.</div>}
            </label>
            <label className="field">
              <span className="field-label">PLAN</span>
              <select className="field-input" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="edit-user-plan">
                <option value="free">free</option>
                <option value="pro">pro</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">SUBSCRIPTION STATUS</span>
              <select className="field-input" value={subStatus} onChange={(e) => setSubStatus(e.target.value)} data-testid="edit-user-sub-status">
                {SUBSCRIPTION_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="field-hint">Tip: "complimentary" grants Pro without billing.</div>
            </label>
            <label className="field field--full">
              <span className="field-label">EMAIL VERIFIED</span>
              <label className="toggle-row">
                <input type="checkbox" checked={emailVerified} onChange={(e) => setEmailVerified(e.target.checked)} data-testid="edit-user-verified" />
                <span>Mark this user's email as verified</span>
              </label>
            </label>
            <label className="field field--full">
              <span className="field-label">TAGS (comma-separated)</span>
              <input className="field-input" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="invited, beta, vip" data-testid="edit-user-tags" />
            </label>
            <label className="field field--full">
              <span className="field-label">ADMIN NOTES</span>
              <textarea className="field-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal-only notes about this account." data-testid="edit-user-notes" />
            </label>
          </div>
          {err && <div className="form-report form-report--err" data-testid="edit-user-error">{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onClose} disabled={busy}>CANCEL</button>
          <button className="btn btn--accent" onClick={save} disabled={busy} data-testid="edit-user-save">
            <Check size={14} weight="bold" /> {busy ? "Saving..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function submit() {
    if (pwd.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true); setErr("");
    try {
      await adminResetPassword(user.id, pwd);
      onDone(`Reset password for ${user.email}.`);
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }
  return (
    <div className="modal-backdrop" data-testid="reset-pw-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel">
        <div className="modal-header">
          <KeyReturn size={16} weight="bold" />
          <span>RESET PASSWORD — {user.email}</span>
          <button className="modal-close" onClick={onClose}><X size={14} weight="bold" /></button>
        </div>
        <div className="modal-body">
          <label className="field">
            <span className="field-label">NEW PASSWORD (min 8 chars)</span>
            <input className="field-input" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Choose a temporary password" data-testid="reset-pw-input" />
          </label>
          <div className="field-hint">Hand this temporary password to the user via a secure channel; they should log in and change it.</div>
          {err && <div className="form-report form-report--err">{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onClose} disabled={busy}>CANCEL</button>
          <button className="btn btn--accent" onClick={submit} disabled={busy} data-testid="reset-pw-submit">
            {busy ? "Saving..." : "RESET PASSWORD"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvitesPanel({ onReport }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [grantPro, setGrantPro] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try { setInvites(await adminListInvites()); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function createInvite() {
    if (!email.trim()) { setErr("Email is required."); return; }
    setBusy(true); setErr("");
    try {
      await adminCreateInvite({ email: email.trim(), grantPro, note });
      setEmail(""); setNote("");
      load();
      onReport(`Invite created for ${email.trim()}.`);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  async function revoke(inv) {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    try {
      await adminRevokeInvite(inv.id);
      onReport(`Revoked invite for ${inv.email}.`);
      load();
    } catch (e) { onReport(`Error: ${e.response?.data?.detail || e.message}`); }
  }

  function inviteUrl(token) {
    return `${window.location.origin}/?invite=${token}`;
  }

  async function copy(token) {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      onReport("Invite link copied to clipboard.");
    } catch (e) { onReport(`Copy failed: ${e.message}`); }
  }

  return (
    <div className="panel" data-testid="admin-invites-panel">
      <div className="panel-header">
        <EnvelopeOpen size={18} weight="bold" />
        <span>INVITES</span>
        <span className="header-count mono">{invites.length}</span>
        <button className="btn btn--outline btn--sm ml-auto" onClick={load} disabled={loading} data-testid="admin-invites-refresh">
          <ArrowsClockwise size={12} weight="bold" /> REFRESH
        </button>
      </div>
      <div className="panel-body">
        <p className="panel-desc">Generate an invite link to onboard a new user. Optionally grant complimentary Pro on signup. Copy the link and send it via your own email/SMS channel.</p>
        <div className="invite-form">
          <input
            className="field-input"
            type="email"
            placeholder="invitee@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="invite-email-input"
          />
          <label className="toggle-row toggle-row--inline">
            <input type="checkbox" checked={grantPro} onChange={(e) => setGrantPro(e.target.checked)} data-testid="invite-grant-pro" />
            <span>Grant complimentary Pro</span>
          </label>
          <input
            className="field-input"
            placeholder="Optional note (e.g. 'beta tester')"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="invite-note-input"
          />
          <button className="btn btn--accent" onClick={createInvite} disabled={busy} data-testid="invite-create-btn">
            <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Creating..." : "CREATE INVITE"}
          </button>
        </div>
        {err && <div className="form-report form-report--err" data-testid="invite-error">{err}</div>}
      </div>

      <div className="panel-body" style={{ padding: 0 }}>
        <div className="table-scroll">
        <table className="admin-table" data-testid="admin-invites-table">
          <thead>
            <tr>
              <th>EMAIL</th>
              <th>PRO?</th>
              <th>NOTE</th>
              <th>CREATED</th>
              <th>STATUS</th>
              <th>LINK</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {[...invites].sort((a, b) => (a.email || "").localeCompare(b.email || "")).map((inv) => (
              <tr key={inv.id} data-testid={`admin-invite-${inv.id}`}>
                <td>{inv.email}</td>
                <td>{inv.grant_pro ? <span className="plan-badge plan-badge--comp">PRO</span> : "—"}</td>
                <td className="small">{inv.note || "—"}</td>
                <td className="mono small">{(inv.createdAt || "").slice(0, 10)}</td>
                <td>
                  {inv.redeemedAt ? (
                    <span className="role-pill role-pill--admin"><Check size={10} weight="bold" /> redeemed</span>
                  ) : (
                    <span className="role-pill">pending</span>
                  )}
                </td>
                <td className="mono small invite-link-cell">
                  {inv.redeemedAt ? "—" : (
                    <code className="invite-link-code">{`/?invite=${inv.token.slice(0, 10)}…`}</code>
                  )}
                </td>
                <td>
                  {!inv.redeemedAt && (
                    <div className="row-actions">
                      <button className="btn btn--outline btn--sm" onClick={() => copy(inv.token)} data-testid={`invite-copy-${inv.id}`}>
                        <Copy size={11} weight="bold" /> COPY LINK
                      </button>
                      <button className="btn btn--danger btn--sm" onClick={() => revoke(inv)} data-testid={`invite-revoke-${inv.id}`}>
                        <Trash size={11} weight="bold" /> REVOKE
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {invites.length === 0 && !loading && (
              <tr><td colSpan="7" className="admin-empty">No invites yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function ConditionSuggestionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const data = await adminListConditionSuggestions();
      setItems(data || []);
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => statusFilter === "all" || (i.status || "pending") === statusFilter);

  async function setStatus(id, status) {
    await adminUpdateConditionSuggestion(id, { status });
    load();
  }
  async function remove(id) {
    if (!window.confirm("Delete this suggestion?")) return;
    await adminDeleteConditionSuggestion(id);
    load();
  }

  function copyAll() {
    const lines = filtered.map((s) => `- ${s.name}${s.code ? ` [DC ${s.code}]` : ""}${s.system ? ` (${s.system})` : ""}  - submitted ${s.submissionCount}x`);
    const text = `Pending condition library additions (${filtered.length})\n${lines.join("\n")}`;
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="panel" data-testid="admin-condition-suggestions-panel" style={{ marginTop: 16 }}>
      <div className="panel-header">
        <BookOpenText size={16} weight="bold" />
        <span>USER-SUBMITTED CONDITIONS (for next library update)</span>
        <span className="header-count mono" style={{ marginLeft: 8 }}>{filtered.length}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <select
            className="field-input"
            style={{ height: 30, fontSize: 12, minWidth: 130, background: "#FFF", color: "#0F172A" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="suggestion-status-filter"
          >
            <option value="pending">Pending</option>
            <option value="added">Added</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button
            className="btn btn--outline btn--sm"
            onClick={copyAll}
            disabled={!filtered.length}
            data-testid="copy-suggestions-btn"
          >
            <Copy size={12} weight="bold" /> COPY LIST
          </button>
          <button className="btn btn--ghost btn--sm" onClick={load} title="Refresh">
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        </div>
      </div>
      <div className="panel-body" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 16, color: "#64748B" }}>Loading...</div>
        ) : err ? (
          <div style={{ padding: 16, color: "#991B1B" }}>{err}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 16, color: "#64748B" }} data-testid="no-suggestions">
            No {statusFilter !== "all" ? statusFilter : ""} suggestions. When users add a custom condition via the Condition Library, it appears here.
          </div>
        ) : (
          <div className="table-scroll">
          <table className="table" data-testid="suggestions-table">
            <thead>
              <tr>
                <th>Condition Name</th>
                <th>DC (guessed)</th>
                <th>System</th>
                <th>Submissions</th>
                <th>Submitters</th>
                <th>Last submitted</th>
                <th>Status</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`suggestion-row-${s.id}`}>
                  <td>
                    <strong>{s.name}</strong>
                    {s.notes && <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>{s.notes}</div>}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>{s.code || "—"}</td>
                  <td style={{ fontSize: 12 }}>{s.system || "—"}</td>
                  <td className="mono" style={{ fontWeight: 800 }} data-testid={`suggestion-count-${s.id}`}>{s.submissionCount}</td>
                  <td style={{ fontSize: 11, color: "#475569" }}>
                    {(s.submitterEmails || []).slice(0, 2).join(", ")}
                    {(s.submitterEmails || []).length > 2 && <span> +{s.submitterEmails.length - 2}</span>}
                  </td>
                  <td style={{ fontSize: 11, color: "#64748B" }}>
                    {s.lastSubmittedAt ? new Date(s.lastSubmittedAt).toLocaleDateString() : "—"}
                  </td>
                  <td>
                    <span
                      className={`rem-status ${
                        s.status === "added" ? "rem-status--ok" :
                        s.status === "rejected" ? "rem-status--overdue" :
                        "rem-status--pending"
                      }`}
                      style={{ fontSize: 10 }}
                    >
                      {(s.status || "pending").toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {(s.status || "pending") === "pending" && (
                      <>
                        <button
                          className="icon-btn"
                          title="Mark added"
                          onClick={() => setStatus(s.id, "added")}
                          data-testid={`suggestion-mark-added-${s.id}`}
                        >
                          <Check size={14} weight="bold" />
                        </button>
                        <button
                          className="icon-btn"
                          title="Reject"
                          onClick={() => setStatus(s.id, "rejected")}
                          data-testid={`suggestion-reject-${s.id}`}
                        >
                          <X size={14} weight="bold" />
                        </button>
                      </>
                    )}
                    {(s.status || "pending") !== "pending" && (
                      <button
                        className="icon-btn"
                        title="Reset to pending"
                        onClick={() => setStatus(s.id, "pending")}
                        data-testid={`suggestion-reopen-${s.id}`}
                      >
                        <ArrowsClockwise size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      className="icon-btn icon-btn--danger"
                      title="Delete"
                      onClick={() => remove(s.id)}
                      data-testid={`suggestion-delete-${s.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function NewsletterPanel({ onReport }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await adminListNewsletterSubscribers();
      setItems(data.items || []);
    } catch (e) {
      onReport(`Error loading newsletter: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) =>
      (s.email || "").toLowerCase().includes(q) ||
      (s.source || "").toLowerCase().includes(q)
    );
  }, [items, filter]);

  async function handleDelete(sub) {
    if (!window.confirm(`Remove ${sub.email} from the newsletter list?`)) return;
    try {
      await adminDeleteNewsletterSubscriber(sub.id);
      onReport(`Removed ${sub.email}.`);
      load();
    } catch (e) {
      onReport(`Error: ${e.response?.data?.detail || e.message}`);
    }
  }

  async function handleDownload() {
    // CSV needs the Authorization header — use fetch + blob to bypass the
    // browser's normal "open in tab" behavior for authed downloads.
    setDownloading(true);
    try {
      const res = await fetch(adminNewsletterExportUrl(), {
        method: "GET",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `claimops-newsletter-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onReport(`Exported ${items.length} subscriber(s) to CSV.`);
    } catch (e) {
      onReport(`Export failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="panel" data-testid="admin-newsletter-panel">
      <div className="panel-header">
        <EnvelopeSimple size={18} weight="bold" />
        <span>NEWSLETTER</span>
        <span className="header-count mono">{items.length}</span>
        <button
          className="btn btn--outline btn--sm ml-auto"
          onClick={load}
          disabled={loading}
          data-testid="admin-newsletter-refresh"
        >
          <ArrowsClockwise size={12} weight="bold" /> REFRESH
        </button>
      </div>
      <div className="panel-body">
        <p className="panel-desc">
          Single-opt-in contact list. Download the CSV anytime to send VA news, app updates, or
          announcements through your own mail tool (Gmail, Mailchimp, Buttondown, etc.).
        </p>
        <div className="admin-newsletter-toolbar">
          <input
            className="field-input"
            placeholder="Filter by email or source..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="admin-newsletter-filter"
          />
          <button
            type="button"
            className="btn btn--accent"
            onClick={handleDownload}
            disabled={downloading || items.length === 0}
            data-testid="admin-newsletter-export"
          >
            <DownloadSimple size={12} weight="bold" />
            {downloading ? "Exporting..." : "Export CSV"}
          </button>
          <span className="admin-newsletter-stat">
            ACTIVE <strong>{items.filter((i) => i.status === "active").length}</strong>
          </span>
        </div>

        {items.length === 0 ? (
          <p className="muted" style={{ marginTop: 12 }}>No subscribers yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="admin-table" data-testid="admin-newsletter-table">
              <thead>
                <tr>
                  <th>EMAIL</th>
                  <th>SOURCE</th>
                  <th>STATUS</th>
                  <th>JOINED</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`admin-newsletter-row-${s.id}`}>
                    <td>{s.email}</td>
                    <td className="mono">{s.source || "-"}</td>
                    <td>{s.status || "active"}</td>
                    <td className="mono">
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "-"}
                    </td>
                    <td>
                      <button
                        className="btn btn--outline btn--sm"
                        onClick={() => handleDelete(s)}
                        data-testid={`admin-newsletter-delete-${s.id}`}
                      >
                        <Trash size={12} weight="bold" /> REMOVE
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminView() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [filter, setFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      const data = await adminListUsers();
      setUsers(data);
    } catch (err) {
      setReport(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const base = filter.trim()
      ? users.filter((u) => {
          const q = filter.trim().toLowerCase();
          return u.email.toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q) ||
            (u.subscription_status || "").toLowerCase().includes(q) ||
            (u.tags || []).join(" ").toLowerCase().includes(q);
        })
      : users;
    return [...base].sort((a, b) => (a.email || "").localeCompare(b.email || ""));
  }, [users, filter]);

  async function handleDelete(u) {
    if (u.id === user.id) { setReport("You cannot delete your own admin account."); return; }
    if (!window.confirm(`Delete user ${u.email} and ALL their workspaces, claim data, and backups? This cannot be undone.`)) return;
    try {
      await adminDeleteUser(u.id);
      setReport(`Deleted ${u.email}.`);
      load();
    } catch (err) {
      setReport(`Error: ${err.response?.data?.detail || err.message}`);
    }
  }

  async function handleGrantPro(u) {
    if (!window.confirm(`Grant complimentary Pro to ${u.email}?`)) return;
    try {
      await adminGrantPro(u.id);
      setReport(`Granted complimentary Pro to ${u.email}.`);
      load();
    } catch (err) { setReport(`Error: ${err.response?.data?.detail || err.message}`); }
  }

  async function handleRevokePro(u) {
    if (!window.confirm(`Revoke complimentary Pro from ${u.email}?`)) return;
    try {
      await adminRevokePro(u.id);
      setReport(`Revoked Pro from ${u.email}.`);
      load();
    } catch (err) { setReport(`Error: ${err.response?.data?.detail || err.message}`); }
  }

  return (
    <div className="view-grid" data-testid="admin-view">
      <div className="panel">
        <div className="panel-header">
          <UsersThree size={18} weight="bold" />
          <span>ADMIN — USERS</span>
          <span className="header-count mono">{filtered.length}/{users.length}</span>
          <input
            className="field-input admin-filter"
            placeholder="Filter by email, role, status, tag..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="admin-filter-input"
          />
          <button className="btn btn--outline btn--sm" onClick={load} disabled={loading} data-testid="admin-refresh-btn">
            <ArrowsClockwise size={12} weight="bold" /> REFRESH
          </button>
        </div>
        <div className="panel-body">
          <p className="panel-desc">All registered accounts. Claim data is encrypted at the user level — you can see counts but not the content of other users' claims.</p>
          {report && <div className="form-report" data-testid="admin-report">{report}</div>}
        </div>
      </div>

      <div className="panel">
        <div className="panel-body" style={{ padding: 0 }}>
          <div className="table-scroll">
            <div className="table-scroll-hint">← scroll →</div>
          <table className="admin-table" data-testid="admin-users-table">
            <thead>
              <tr>
                <th>EMAIL</th>
                <th>ROLE</th>
                <th>PLAN</th>
                <th>VERIFIED</th>
                <th className="mono">WS</th>
                <th className="mono">BK</th>
                <th>TAGS</th>
                <th>CREATED</th>
                <th>LAST LOGIN</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSelf = u.id === user.id;
                const isPro = u.is_pro || u.plan === "pro";
                const isPaid = ["active", "trialing", "past_due"].includes(u.subscription_status);
                return (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                    <td>
                      {u.email}
                      {isSelf && <span className="admin-you-tag">YOU</span>}
                      {u.notes && <span className="admin-note-tag" title={u.notes}>•</span>}
                    </td>
                    <td>
                      <span className={`role-pill ${u.role === "admin" ? "role-pill--admin" : ""}`}>
                        {u.role === "admin" && <ShieldCheck size={10} weight="bold" />}
                        {u.role}
                      </span>
                    </td>
                    <td><PlanBadge user={u} /></td>
                    <td>{u.emailVerified ? "✓" : "—"}</td>
                    <td className="mono">{u.workspaceCount}</td>
                    <td className="mono">{u.backupCount}</td>
                    <td className="small">{(u.tags || []).join(", ") || "—"}</td>
                    <td className="mono small">{(u.createdAt || "").slice(0, 10)}</td>
                    <td className="mono small">{(u.lastLoginAt || "").slice(0, 10) || "—"}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn--outline btn--sm" onClick={() => setEditTarget(u)} data-testid={`admin-edit-${u.id}`} title="Edit user">
                          <PencilSimple size={11} weight="bold" />
                        </button>
                        <button className="btn btn--outline btn--sm" onClick={() => setResetTarget(u)} data-testid={`admin-reset-pw-${u.id}`} title="Reset password">
                          <KeyReturn size={11} weight="bold" />
                        </button>
                        {!isPro && (
                          <button className="btn btn--accent btn--sm" onClick={() => handleGrantPro(u)} data-testid={`admin-grant-pro-${u.id}`} title="Grant complimentary Pro">
                            <Crown size={11} weight="bold" /> GRANT PRO
                          </button>
                        )}
                        {isPro && !isPaid && !isSelf && u.role !== "admin" && (
                          <button className="btn btn--outline btn--sm" onClick={() => handleRevokePro(u)} data-testid={`admin-revoke-pro-${u.id}`} title="Revoke complimentary Pro">
                            <Crown size={11} weight="bold" /> REVOKE
                          </button>
                        )}
                        {!isSelf && (
                          <button className="btn btn--danger btn--sm" onClick={() => handleDelete(u)} data-testid={`admin-delete-${u.id}`} title="Delete user">
                            <Trash size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan="10" className="admin-empty">No matching users.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <InvitesPanel onReport={setReport} />

      <NewsletterPanel onReport={setReport} />

      <ConditionSuggestionsPanel />

      {editTarget && (
        <EditUserModal
          user={editTarget}
          isSelf={editTarget.id === user.id}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setReport(`Updated ${editTarget.email}.`); load(); }}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={(msg) => setReport(msg)}
        />
      )}
    </div>
  );
}
